// src/utils/analytics.js
// Google Analytics 4 utility functions
// ------------------------------------
// Works with gtag.js snippet loaded in index.html
// Usage:
//   import { initAnalytics, trackPageView, trackEvent } from '@/utils/analytics'
//   initAnalytics('G-XXXXXXX')  // once on load
//   trackPageView(window.location.pathname)
//   trackEvent('cta_click', { cta_name: 'Get Demo' })

// Check if gtag is available
const hasGtag = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * Initialize GA4 configuration
 * (Use send_page_view:false so we control pageview firing for SPAs)
 */
export const initAnalytics = (measurementId) => {
  if (!measurementId || !hasGtag()) return;
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    send_page_view: false,
  });
};

/**
 * Track a page view (call on every route change)
 */
export const trackPageView = (url) => {
  if (!hasGtag()) return;
  window.gtag('event', 'page_view', {
    page_path: url,
    page_location: window.location.href,
    page_title: document.title,
  });
};

/**
 * Track a custom GA4 event
 * @param {string} name  event name (e.g. 'cta_click')
 * @param {object} params optional event parameters
 */
export const trackEvent = (name, params = {}) => {
  if (!hasGtag()) return;
  window.gtag('event', name, params);
};

/**
 * Track an outbound link click
 */
export const trackOutboundLink = (url) => {
  trackEvent('click', { category: 'outbound', label: url });
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (formName) => {
  trackEvent('submit', { category: 'form', label: formName });
};

/**
 * Track newsletter signups
 */
export const trackNewsletterSignup = () => {
  trackEvent('signup', { category: 'newsletter', label: 'blog_page' });
};

/**
 * Track contact form submission
 */
export const trackContactSubmission = (subject) => {
  trackEvent('submit', { category: 'contact_form', label: subject });
};

/**
 * Track scroll depth (e.g., 25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll', {
    category: 'engagement',
    label: `${percentage}%`,
    value: percentage,
  });
};

/**
 * Track time on page
 */
export const trackTimeOnPage = (seconds) => {
  trackEvent('time_on_page', { category: 'engagement', value: seconds });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName, location) => {
  trackEvent('cta_click', {
    category: 'engagement',
    label: `${ctaName}_${location}`,
  });
};
